import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { PurchaseDetail, PurchaseRow, PurchaseStatus } from '../api/types';
import { StatusBadge } from '../components/Badge';
import { Card, KeyValue, PageHeader } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Select } from '../components/Form';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, dateTime, money, num, seconds, text } from '../lib/format';

const STATUS_OPTIONS: Array<{ value: '' | PurchaseStatus; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'queued', label: 'В очереди' },
  { value: 'processing', label: 'В работе' },
  { value: 'delivered', label: 'Выдан' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'canceled', label: 'Отменён' },
  { value: 'refunded', label: 'Возврат' },
];

export function PurchasesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [status, setStatus] = useState<'' | PurchaseStatus>('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('Manual refund');

  const path = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (status) params.set('status', status);
    if (clientId) params.set('client_id', clientId);
    if (projectId) params.set('project_id', projectId);
    if (search) params.set('search', search);
    return `/admin-api/purchases?${params.toString()}`;
  }, [page, limit, status, clientId, projectId, search]);

  const listQuery = useQuery<{ list: PurchaseRow[]; total: number }, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const detailPath = selectedId ? `/admin-api/purchases/${selectedId}` : null;
  const detailQuery = useQuery<PurchaseDetail, ApiError>({
    queryKey: ['api', detailPath ?? ''],
    queryFn: ({ signal }) => api.get<PurchaseDetail>(detailPath!, signal),
    enabled: selectedId !== null,
    retry: 1,
  });

  const refreshAll = () => {
    void invalidatePrefix(queryClient, '/admin-api/purchases');
    void invalidatePrefix(queryClient, '/admin-api/queue');
    void invalidatePrefix(queryClient, '/admin-api/stats');
  };

  const doRefund = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/admin-api/purchases/${selectedId}/refund`, { reason: refundReason });
      toast.success('Возврат выполнен');
      setRefundOpen(false);
      refreshAll();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка возврата');
    }
  };

  const doCancel = async (id: string) => {
    const ok = await confirm({
      title: 'Отменить покупку?',
      body: `Покупка ${id.slice(0, 8)}… будет отменена.`,
      confirmLabel: 'Отменить',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/admin-api/purchases/${id}/cancel`);
      toast.success('Покупка отменена');
      refreshAll();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const list = listQuery.data?.list ?? [];
  const total = listQuery.data?.total ?? 0;
  const detail = detailQuery.data;

  return (
    <div className="space-y-4">
      <PageHeader title="Покупки" description={`Всего: ${num(total)}`} />

      <Card title="Фильтры" bodyClassName="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Статус">
            <Select value={status} onChange={(e) => { setStatus(e.target.value as '' | PurchaseStatus); setPage(1); }}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
          <Field label="ID клиента">
            <Input value={clientId} onChange={(e) => { setClientId(e.target.value); setPage(1); }} placeholder="напр. 1" inputMode="numeric" />
          </Field>
          <Field label="ID проекта">
            <Input value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(1); }} placeholder="напр. 532" inputMode="numeric" />
          </Field>
          <Field label="Поиск">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(e.currentTarget.value.trim()); setPage(1); } }}
              placeholder="телефон или ID"
            />
          </Field>
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={() => { setSearch(searchInput.trim()); setPage(1); }}>Найти</Button>
            <Button variant="ghost" onClick={() => { setStatus(''); setClientId(''); setProjectId(''); setSearch(''); setSearchInput(''); setPage(1); }}>Сбросить</Button>
          </div>
        </div>
      </Card>

      <Card noPadding>
        {listQuery.error ? (
          <div className="p-3"><ErrorBanner message={listQuery.error.message} onRetry={() => listQuery.refetch()} /></div>
        ) : listQuery.isLoading && !list.length ? (
          <LoadingBlock />
        ) : list.length === 0 ? (
          <EmptyState title="Покупки не найдены" description="Измените фильтры или сбросьте их." />
        ) : (
          <>
            <DataTable<PurchaseRow>
              columns={[
                { key: 'purchase_id', header: 'ID', width: '8rem', render: (r) => <Mono className="text-[10px]">{r.purchase_id.slice(0, 8)}…</Mono> },
                { key: 'status', header: 'Статус', width: '5rem', render: (r) => <StatusBadge kind="purchase" status={r.status} /> },
                { key: 'client', header: 'Клиент', hide: 'sm', render: (r) => <span className="truncate">{r.client}</span> },
                { key: 'project', header: 'Проект', hide: 'md', render: (r) => <span className="truncate">{r.project}</span> },
                { key: 'tel', header: 'Телефон', hide: 'lg', render: (r) => <Mono>{r.tel ?? '—'}</Mono> },
                { key: 'price', header: 'Цена', align: 'right', render: (r) => money(r.price) },
                { key: 'cost', header: 'Себест.', align: 'right', hide: 'xl', render: (r) => money(r.cost) },
                { key: 'delivery', header: 'Выдача', align: 'right', hide: 'md', width: '5rem', render: (r) => seconds(r.delivery_seconds) },
                { key: 'created', header: 'Создан', align: 'right', hide: 'lg', width: '7rem', render: (r) => ago(r.created_at) },
              ]}
              rows={list}
              rowKey={(r) => r.purchase_id}
              onRowClick={(r) => setSelectedId(r.purchase_id)}
              loading={listQuery.isFetching}
              minWidth="40rem"
            />
            <div className="border-t border-zinc-800 px-3 py-2 sm:px-4">
              <Pagination page={page} limit={limit} total={total} count={list.length} onPage={setPage} onLimit={(v) => { setLimit(v); setPage(1); }} />
            </div>
          </>
        )}
      </Card>

      <Modal open={selectedId !== null} onClose={() => setSelectedId(null)} title="Детали покупки" variant="drawer" size="lg">
        {detailQuery.isLoading ? <LoadingBlock /> : detailQuery.error ? <ErrorBanner message={detailQuery.error.message} /> : detail ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <StatusBadge kind="purchase" status={detail.purchase.status} />
              <Mono className="text-[10px]">{detail.purchase.id}</Mono>
              <CopyButton value={detail.purchase.id} label="ID" size="sm" />
            </div>

            <KeyValue
              items={[
                { label: 'Клиент', value: detail.client?.name ?? '—' },
                { label: 'Проект', value: detail.project?.name ?? '—' },
                { label: 'Тип', value: detail.purchase.type === 1 ? 'SMS' : detail.purchase.type === 2 ? 'аренда' : `type ${detail.purchase.type}` },
                { label: 'App ID', value: detail.purchase.appUpstreamId },
                { label: 'Цена', value: money(detail.purchase.price) },
                { label: 'Себестоимость', value: money(detail.purchase.cost) },
                { label: 'Попытки', value: num(detail.purchase.attempts) },
                { label: 'Зарезервировано', value: money(detail.purchase.reserved) },
                { label: 'Создан', value: dateTime(detail.purchase.queuedAt) },
                { label: 'Выдан', value: dateTime(detail.purchase.deliveredAt) },
                { label: 'Deadline', value: dateTime(detail.purchase.deadlineAt) },
                { label: 'Завершён', value: dateTime(detail.purchase.settledAt) },
              ]}
            />

            {detail.purchase.lastError ? (
              <div className="rounded-lg border border-red-900/60 bg-red-950/40 p-3 text-xs text-red-300">
                <div className="mb-1 font-medium text-red-200">Ошибка</div>
                {text(detail.purchase.lastError)}
              </div>
            ) : null}

            {detail.number ? (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Номер</h3>
                <KeyValue
                  items={[
                    { label: 'Телефон', value: <Mono>{detail.number.tel}</Mono> },
                    { label: 'Статус', value: <StatusBadge kind="number" status={detail.number.status} /> },
                    { label: 'SMS', value: num(detail.number.sms_count) },
                    { label: 'Ordernum', value: <Mono className="text-[10px]">{detail.number.ordernum}</Mono> },
                    { label: 'Истекает', value: dateTime(detail.number.end_time) },
                    { label: 'Первый код', value: dateTime(detail.number.first_code_at) },
                  ]}
                />
              </div>
            ) : null}

            {detail.batch ? (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Батч</h3>
                <KeyValue
                  items={[
                    { label: 'ID', value: String(detail.batch.id ?? '—') },
                    { label: 'Статус', value: <StatusBadge kind="batch" status={(detail.batch.status as string) ?? '—'} /> },
                    { label: 'Ordernum', value: <Mono className="text-[10px]">{(detail.batch.ordernum as string) ?? '—'}</Mono> },
                    { label: 'App ID', value: String(detail.batch.appUpstreamId ?? '—') },
                  ]}
                />
              </div>
            ) : null}

            {detail.sms.length > 0 ? (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">SMS-коды</h3>
                <DataTable
                  columns={[
                    { key: 'code', header: 'Код', render: (r: typeof detail.sms[number]) => <Mono>{r.code ?? '—'}</Mono> },
                    { key: 'time', header: 'Время', align: 'right', render: (r) => dateTime(r.created_at) },
                  ]}
                  rows={detail.sms}
                  rowKey={(r) => r.id}
                  minWidth="18rem"
                />
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 border-t border-zinc-800 pt-3">
              <Button variant="danger" size="sm" onClick={() => setRefundOpen(true)}>Возврат</Button>
              <Button variant="secondary" size="sm" onClick={() => doCancel(detail.purchase.id)}>Отменить</Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Возврат покупки"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundOpen(false)}>Отмена</Button>
            <Button variant="danger" onClick={doRefund}>Вернуть</Button>
          </>
        }
      >
        {detail ? (
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">
              Возврат для покупки <Mono className="text-[10px]">{detail.purchase.id.slice(0, 8)}…</Mono> — клиенту будет возвращено {money(detail.purchase.price)}.
            </p>
            <Field label="Причина">
              <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
            </Field>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
